// Natural hair-shade palette for the VTO hair-recolour feature (MVP).
//
// `suits` tags the subtone each shade flatters; the result UI surfaces the
// shades matching the SELFIE'S detected subtone (subtone.class) as
// "recommended for you". Names are kept as universal beauty terms (like
// foundation shade names) rather than localised per language.
//
// Note on the recolour: the pass preserves luminance (see hairOverlay), so the
// lighter shades (honey/sand/platinum) read as a tint on already-light hair and
// only subtly on dark hair — the UI carries an honest "doesn't lighten" note.

export type HairSuit = 'warm' | 'cool' | 'neutral' | 'all';

export interface HairShade {
  id: string;
  name: string;
  hex: string;
  suits: HairSuit;
}

// The recolour preserves luminance (Canvas2D `color` blend), so it can't LIFT:
// a shade lighter than the hair reads only as a tint, never a true lightening.
// That caveat ("doesn't lighten dark hair") is only relevant for the light
// shades — surface it contextually for those, not for medium/dark choices where
// it's just confusing. Threshold on perceived luminance cleanly selects the
// blondes/platinum (honey 122 / sand 132 / platinum 180) over caramel/copper
// (~80) and everything darker.
function shadeLuma(shade: HairShade): number {
  const hex = shade.hex.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function hairShadeNeedsLift(shade: HairShade): boolean {
  return shadeLuma(shade) >= 110;
}

// The mirror caveat of hairShadeNeedsLift: the `color` blend preserves
// luminance, so it also can't DARKEN. A genuinely dark shade (espresso,
// blue-black, burgundy…) on light or grey hair keeps that hair's lightness and
// reads only as a reflection/tint, never a true dark colour. Surface an honest
// note for those — luma < 70 cleanly selects the dark end (espresso 30 …
// ash-brown 66) and leaves the mid/light shades alone.
export function hairShadeIsDark(shade: HairShade): boolean {
  return shadeLuma(shade) < 70;
}

export const HAIR_SHADES: readonly HairShade[] = [
  { id: 'espresso',      name: 'Espresso',      hex: '#2a1a12', suits: 'all' },
  { id: 'blue-black',    name: 'Blue Black',    hex: '#14161c', suits: 'cool' },
  { id: 'chocolate',     name: 'Chocolate',     hex: '#3b2417', suits: 'warm' },
  { id: 'ash-brown',     name: 'Ash Brown',     hex: '#4a4038', suits: 'cool' },
  { id: 'chestnut',      name: 'Chestnut',      hex: '#5a3520', suits: 'neutral' },
  { id: 'caramel',       name: 'Caramel',       hex: '#7a4a24', suits: 'warm' },
  { id: 'mahogany',      name: 'Mahogany',      hex: '#5a2222', suits: 'warm' },
  { id: 'burgundy',      name: 'Burgundy',      hex: '#4a1626', suits: 'cool' },
  { id: 'copper',        name: 'Copper',        hex: '#8a3b1a', suits: 'warm' },
  { id: 'honey',         name: 'Honey Blonde',  hex: '#a9712f', suits: 'warm' },
  { id: 'sand',          name: 'Sand Blonde',   hex: '#9a8254', suits: 'neutral' },
  { id: 'cool-platinum', name: 'Cool Platinum', hex: '#b9b3a8', suits: 'cool' }
];
